import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Pencil, Trash2, ChevronDown, ChevronUp, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { stock } from '@/lib/api';

export function ProductOptionsManager({ productId, productName }) {
  const [optionGroups, setOptionGroups] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showOptionModal, setShowOptionModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingOption, setEditingOption] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  
  const [groupForm, setGroupForm] = useState({
    name: '',
    min_quantity: 0,
    max_quantity: 10,
    is_required: false,
    sort_order: 0
  });
  
  const [optionForm, setOptionForm] = useState({
    name: '',
    external_id: '',
    price_modifier: 0,
    stock_item_id: '',
    quantity_per_unit: 1,
    is_removal: false,
    is_default: false,
    is_available: true,
    sort_order: 0
  });

  useEffect(() => {
    if (productId) {
      loadOptionGroups();
      loadStockItems();
    }
  }, [productId]);

  const loadOptionGroups = async () => {
    try {
      setLoading(true);
      const response = await stock.getProductOptionGroups(productId);
      // Handle different response formats
      const groups = Array.isArray(response) ? response : (response?.data || []);
      setOptionGroups(groups);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
      setOptionGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStockItems = async () => {
    try {
      const response = await stock.getItems();
      // Handle different response formats
      const items = Array.isArray(response) ? response : (response?.data || response?.items || []);
      setStockItems(items);
    } catch (error) {
      console.error('Erro ao carregar itens de estoque:', error);
      setStockItems([]);
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const handleOpenGroupModal = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setGroupForm({
        name: group.name,
        min_quantity: group.min_quantity,
        max_quantity: group.max_quantity,
        is_required: group.is_required,
        sort_order: group.sort_order
      });
    } else {
      setEditingGroup(null);
      setGroupForm({
        name: '',
        min_quantity: 0,
        max_quantity: 10,
        is_required: false,
        sort_order: optionGroups.length
      });
    }
    setShowGroupModal(true);
  };

  const handleSaveGroup = async () => {
    try {
      if (editingGroup) {
        toast.info('Atualização de grupo em desenvolvimento');
      } else {
        await stock.createOptionGroup(productId, groupForm);
        toast.success('Grupo criado com sucesso!');
      }
      setShowGroupModal(false);
      loadOptionGroups();
    } catch (error) {
      toast.error('Erro ao salvar grupo');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Tem certeza que deseja excluir este grupo e todas suas opções?')) return;
    try {
      await stock.deleteOptionGroup(groupId);
      toast.success('Grupo excluído!');
      loadOptionGroups();
    } catch (error) {
      toast.error('Erro ao excluir grupo');
    }
  };

  const handleOpenOptionModal = (groupId, option = null) => {
    setSelectedGroupId(groupId);
    if (option) {
      setEditingOption(option);
      setOptionForm({
        name: option.name,
        external_id: option.external_id || '',
        price_modifier: option.price_modifier || 0,
        stock_item_id: option.stock_item_id || '',
        quantity_per_unit: option.quantity_per_unit || 1,
        is_removal: option.is_removal || false,
        is_default: option.is_default || false,
        is_available: option.is_available !== false,
        sort_order: option.sort_order || 0
      });
    } else {
      setEditingOption(null);
      const group = optionGroups.find(g => g.id === groupId);
      setOptionForm({
        name: '',
        external_id: '',
        price_modifier: 0,
        stock_item_id: '',
        quantity_per_unit: 1,
        is_removal: false,
        is_default: false,
        is_available: true,
        sort_order: group?.options?.length || 0
      });
    }
    setShowOptionModal(true);
  };

  const handleSaveOption = async () => {
    try {
      if (editingOption) {
        await stock.updateOption(editingOption.id, optionForm);
        toast.success('Opção atualizada!');
      } else {
        await stock.createOption(selectedGroupId, optionForm);
        toast.success('Opção criada!');
      }
      setShowOptionModal(false);
      loadOptionGroups();
    } catch (error) {
      toast.error('Erro ao salvar opção');
    }
  };

  const handleDeleteOption = async (optionId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta opção?')) return;
    try {
      await stock.deleteOption(optionId);
      toast.success('Opção excluída!');
      loadOptionGroups();
    } catch (error) {
      toast.error('Erro ao excluir opção');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  if (!productId) {
    return (
      <div className="text-center py-8 text-slate-500">
        Salve o produto primeiro para gerenciar variações.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Variações e Opções</h3>
          <p className="text-sm text-slate-500">
            Configure adicionais, tamanhos, bordas e outras opções do produto
          </p>
        </div>
        <Button type="button" onClick={() => handleOpenGroupModal()} className="bg-[#FF6B6B] hover:bg-[#ff5252]">
          <Plus className="w-4 h-4 mr-2" />
          Novo Grupo
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8">Carregando...</div>
      ) : optionGroups.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
          <Settings className="w-12 h-12 mx-auto text-slate-400 mb-4" />
          <p className="text-slate-600 font-medium">Nenhum grupo de opções</p>
          <p className="text-sm text-slate-500 mb-4">
            Crie grupos como &quot;Tamanho&quot;, &quot;Adicionais&quot;, &quot;Remover ingredientes&quot;
          </p>
          <Button type="button" onClick={() => handleOpenGroupModal()} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Criar primeiro grupo
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {(Array.isArray(optionGroups) ? optionGroups : []).map((group) => (
            <div key={group.id} className="border rounded-lg bg-white overflow-hidden">
              <div 
                className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100"
                onClick={() => toggleGroup(group.id)}
              >
                <div className="flex items-center gap-3">
                  {expandedGroups[group.id] ? 
                    <ChevronUp className="w-5 h-5 text-slate-400" /> : 
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  }
                  <div>
                    <span className="font-semibold text-slate-900">{group.name}</span>
                    <div className="flex gap-3 text-xs text-slate-500">
                      <span>{group.options?.length || 0} opções</span>
                      {group.is_required && <span className="text-amber-600">Obrigatório</span>}
                      <span>Min: {group.min_quantity} | Max: {group.max_quantity}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                  <Button type="button" size="sm" variant="ghost" onClick={() => handleOpenOptionModal(group.id)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => handleOpenGroupModal(group)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button type="button" size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteGroup(group.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {expandedGroups[group.id] && (
                <div className="p-4">
                  {group.options?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nome</TableHead>
                          <TableHead>Preço</TableHead>
                          <TableHead>Item de Estoque</TableHead>
                          <TableHead>Qtd/Un</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead className="w-[100px]">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(Array.isArray(group.options) ? group.options : []).map((option) => {
                          const stockItem = (Array.isArray(stockItems) ? stockItems : []).find(s => s.id === option?.stock_item_id);
                          if (!option) return null;
                          return (
                            <TableRow key={option.id}>
                              <TableCell className="font-medium">
                                {option.name}
                                {option.is_default && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Padrão</span>}
                                {!option.is_available && <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Indisponível</span>}
                              </TableCell>
                              <TableCell>
                                {option.price_modifier > 0 ? (
                                  <span className="text-green-600">+{formatCurrency(option.price_modifier)}</span>
                                ) : option.price_modifier < 0 ? (
                                  <span className="text-red-600">{formatCurrency(option.price_modifier)}</span>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {stockItem ? (
                                  <span className="text-sm">{stockItem.name}</span>
                                ) : (
                                  <span className="text-slate-400 text-sm">Não vinculado</span>
                                )}
                              </TableCell>
                              <TableCell>{option.quantity_per_unit}</TableCell>
                              <TableCell>
                                {option.is_removal ? (
                                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Remoção</span>
                                ) : (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Adicional</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button type="button" size="sm" variant="ghost" onClick={() => handleOpenOptionModal(group.id, option)}>
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button type="button" size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteOption(option.id)}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-6 text-slate-500">
                      <p>Nenhuma opção neste grupo</p>
                      <Button type="button" size="sm" variant="link" onClick={() => handleOpenOptionModal(group.id)}>
                        Adicionar opção
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={showGroupModal} onOpenChange={setShowGroupModal}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle>{editingGroup ? 'Editar Grupo' : 'Novo Grupo de Opções'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome do Grupo *</Label>
              <Input
                value={groupForm.name}
                onChange={e => setGroupForm({...groupForm, name: e.target.value})}
                placeholder="Ex: Tamanho, Adicionais, Remover..."
                className="bg-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Mínimo</Label>
                <Input
                  type="number"
                  value={groupForm.min_quantity}
                  onChange={e => setGroupForm({...groupForm, min_quantity: parseInt(e.target.value) || 0})}
                  className="bg-white"
                />
              </div>
              <div>
                <Label>Máximo</Label>
                <Input
                  type="number"
                  value={groupForm.max_quantity}
                  onChange={e => setGroupForm({...groupForm, max_quantity: parseInt(e.target.value) || 1})}
                  className="bg-white"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="is_required"
                checked={groupForm.is_required}
                onCheckedChange={checked => setGroupForm({...groupForm, is_required: checked})}
              />
              <Label htmlFor="is_required">Obrigatório</Label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowGroupModal(false)}>Cancelar</Button>
            <Button type="button" onClick={handleSaveGroup} className="bg-[#FF6B6B] hover:bg-[#ff5252]">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showOptionModal} onOpenChange={setShowOptionModal}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingOption ? 'Editar Opção' : 'Nova Opção'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <Label>Nome da Opção *</Label>
              <Input
                value={optionForm.name}
                onChange={e => setOptionForm({...optionForm, name: e.target.value})}
                placeholder="Ex: Grande, Bacon Extra, Sem Cebola..."
                className="bg-white"
              />
            </div>
            
            <div>
              <Label>ID Externo (99Food/iFood)</Label>
              <Input
                value={optionForm.external_id}
                onChange={e => setOptionForm({...optionForm, external_id: e.target.value})}
                placeholder="Ex: opcao_bacon_v3"
                className="bg-white"
              />
              <p className="text-xs text-slate-500 mt-1">Usado para vincular com pedidos das plataformas</p>
            </div>

            <div>
              <Label>Modificador de Preço (R$)</Label>
              <Input
                type="number"
                step="0.01"
                value={optionForm.price_modifier}
                onChange={e => setOptionForm({...optionForm, price_modifier: parseFloat(e.target.value) || 0})}
                className="bg-white"
              />
              <p className="text-xs text-slate-500 mt-1">Valor adicional ou desconto (use negativo)</p>
            </div>

            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Vínculo com Estoque (CMV)</Label>
              <p className="text-xs text-slate-500 mb-3">Para baixa automática de estoque quando vendido</p>
              
              <div>
                <Label>Item de Estoque</Label>
                <Select
                  value={optionForm.stock_item_id || "__none__"}
                  onValueChange={value => setOptionForm({...optionForm, stock_item_id: value === "__none__" ? "" : value})}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Selecione o item de estoque..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nenhum (sem baixa)</SelectItem>
                    {stockItems.filter(item => item && item.id).map(item => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name} ({item.base_unit})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-3">
                <Label>Quantidade por Unidade Vendida</Label>
                <Input
                  type="number"
                  step="0.001"
                  value={optionForm.quantity_per_unit}
                  onChange={e => setOptionForm({...optionForm, quantity_per_unit: parseFloat(e.target.value) || 1})}
                  className="bg-white"
                />
                <p className="text-xs text-slate-500 mt-1">Ex: 0.05 = 50g de bacon por unidade</p>
              </div>
            </div>

            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_removal"
                  checked={optionForm.is_removal}
                  onCheckedChange={checked => setOptionForm({...optionForm, is_removal: checked})}
                />
                <Label htmlFor="is_removal">É uma remoção (ex: &quot;Sem cebola&quot;)</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_default"
                  checked={optionForm.is_default}
                  onCheckedChange={checked => setOptionForm({...optionForm, is_default: checked})}
                />
                <Label htmlFor="is_default">Selecionado por padrão</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_available"
                  checked={optionForm.is_available}
                  onCheckedChange={checked => setOptionForm({...optionForm, is_available: checked})}
                />
                <Label htmlFor="is_available">Disponível para venda</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowOptionModal(false)}>Cancelar</Button>
            <Button type="button" onClick={handleSaveOption} className="bg-[#FF6B6B] hover:bg-[#ff5252]">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProductOptionsManager;
